import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { trackEvent } from "@/lib/analytics/posthog";
import { SEO } from "@/components/seo";

type UniLogo = { src: string; alt: string };

export default function Home() {
  const [logos, setLogos] = useState<UniLogo[]>([]);
  const [location] = useLocation();
  const isHome = location === "/";

  useEffect(() => {
    let mounted = true;
    fetch("/assets/university_logos.json", { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Failed to load logos"))))
      .then((data: any) => {
        // Accept both array shape and object shape { logos: [...] }
        const arr: UniLogo[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.logos)
          ? data.logos
          : [];
        if (mounted) setLogos(arr);
      })
      .catch(() => {
        // fail silently for now in case asset not present in dev
      });
    return () => {
      mounted = false;
    };
  }, []);

  const duplicated = logos.length ? [...logos, ...logos] : [];

  return (
    <div className="bg-black text-white">
      <SEO
        title="BounceBunny - Your NBA-Level Athletic Performance Coach"
        description="Get personalized AI-powered basketball training plans to jump higher, get stronger, and stay injury-free. Trusted by athletes from top universities."
        canonical="https://bouncebunny.app"
      />
      <main id="main-content">
        <section className="hero" aria-label="Intro">
          <div className="hero-media" aria-hidden="true">
            {isHome && (
              <>
                <video className="hero-video" autoPlay muted loop playsInline preload="auto">
                  {/* Mobile first */}
                  <source src="/assets/dunk_mobile.mp4" type="video/mp4" media="(max-width: 767.98px)" />
                  {/* Desktop fallback (using mobile-encoded video for broader compatibility) */}
                  <source src="/assets/dunk_mobile.mp4" type="video/mp4" media="(min-width: 768px)" />
                  {/* Ultimate fallback with no media query */}
                  <source src="/assets/dunk_mobile.mp4" type="video/mp4" />
                </video>
                <div className="video-overlay"></div>
              </>
            )}
          </div>
          <div className="hero-content container">
            <h1 className="title-1">
              <span className="hero-line">Your <span className="hover-spotlight">NBA-Level</span></span>
              <span className="hero-line">Athletic Performance Coach</span>
            </h1>
            <p className="title-2">Jump Higher, Get Stronger, Stay Injury-Free</p>
            <Link
              className="btn btn-outline cta-main"
              href="/form"
              onClick={() => {
                try {
                  trackEvent("home_cta_clicked", { source: "hero_cta" });
                } catch {}
              }}
            >
              Get Your Training Plan For Free
            </Link>
          </div>
        </section>

        <section className="trusted" aria-label="Trusted by">
          <p className="trusted-text">TRUSTED BY ATHLETES FROM</p>
          <div className="logos-carousel">
            <div className="logos-track" id="logos-track">
              {duplicated.map((logo, idx) => {
                const src = logo.src.startsWith("/") ? logo.src : `/${logo.src}`;
                return (
                  <div className="logo-item" key={`${idx}-${src}`}>
                    <img src={src} alt={logo.alt} loading="lazy" decoding="async" />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

